/**
 * useOrgUnitsPublic Hook
 *
 * **V40: 일반 사용자용 조직 목록 조회 (Firestore 직접 조회)**
 *
 * - 모든 로그인 사용자가 조직 목록을 조회할 수 있음
 * - Firestore Rules: orgUnits는 isAuthenticated()면 read 허용
 * - 조직도 페이지(/organization)에서 사용
 *
 * **vs useOrgUnits:**
 * - useOrgUnits: Cloud Function 경유 (관리자 전용, 회사 스코프 필터)
 * - useOrgUnitsPublic: Firestore 직접 조회 (일반 사용자용)
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { OrgUnit } from '../types/orgUnit';
import { getCached, setCached, CACHE_KEY } from '../utils/masterDataCache';

/**
 * 필터 옵션
 */
export interface OrgUnitsPublicFilter {
  /** active 상태만 조회 */
  isActive?: boolean;
  /** 회사 코드 필터 (optional) */
  companyCode?: 'KR' | 'CN';
}

/**
 * Hook 반환 타입
 */
export interface UseOrgUnitsPublicResult {
  orgUnits: OrgUnit[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * V40: 일반 사용자용 조직 목록 조회 훅
 *
 * Firestore orgUnits 컬렉션을 직접 조회합니다.
 * 모든 로그인 사용자가 사용 가능합니다.
 */
export function useOrgUnitsPublic(filters?: OrgUnitsPublicFilter): UseOrgUnitsPublicResult {
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrgUnits = async () => {
    // Firebase가 초기화되지 않은 경우
    if (!db) {
      setOrgUnits([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // V259: 캐시 키 — Firestore 전체 조회이므로 고정 키
      const cacheKey = `${CACHE_KEY.ORG_UNITS_PUBLIC}all`;
      let allOrgUnits: OrgUnit[];

      // V259: 캐시 적중 시 Firestore getDocs 스킵
      const cached = getCached<OrgUnit[]>(cacheKey);
      if (cached) {
        allOrgUnits = cached;
      } else {
        // 기본 쿼리: order로 정렬
        let q = query(collection(db, 'orgUnits'), orderBy('order', 'asc'));

        // Firestore는 복합 쿼리에 제한이 있으므로 클라이언트 필터링 사용
        const snapshot = await getDocs(q);

        allOrgUnits = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || '',
            code: data.code || '',
            type: data.type || 'team',
            parentId: data.parentId || null,
            status: data.status || 'active',
            order: data.order || 0,
            level: data.level || 1,
            path: typeof data.path === 'string' ? data.path : (Array.isArray(data.path) ? '/' + data.path.join('/') : ''),
            companyCode: data.companyCode,
            orgManagerEmployeeId: data.orgManagerEmployeeId,
            approverEmployeeId: data.approverEmployeeId,
            approverName: data.approverName,
            leaderId: data.leaderId,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            createdBy: data.createdBy || '',
            updatedBy: data.updatedBy || '',
          } as OrgUnit;
        });

        // V259: 캐시 저장
        setCached(cacheKey, allOrgUnits);
      }

      // 클라이언트 필터링
      let results = [...allOrgUnits];

      if (filters?.isActive) {
        results = results.filter((org) => org.status === 'active');
      }

      if (filters?.companyCode) {
        results = results.filter((org) => org.companyCode === filters.companyCode);
      }

      setOrgUnits(results);
    } catch (err: any) {
      console.error('[useOrgUnitsPublic] 조직 목록 조회 실패:', err);

      if (err?.code === 'permission-denied') {
        setError(new Error('조직 목록 조회 권한이 없습니다. 로그인이 필요합니다.'));
      } else {
        setError(err instanceof Error ? err : new Error('조직 목록 조회 중 오류가 발생했습니다.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.isActive, filters?.companyCode]);

  return {
    orgUnits,
    isLoading,
    error,
    refetch: fetchOrgUnits,
  };
}

export default useOrgUnitsPublic;
